/* ============================================================
   VEYLA | listicle G, "the switch" : THE SOURCE REGISTER

   The article copy on this page is a SUPPLIED SCRIPT (SCRIPT.docx, kept beside
   the page as provenance), used on Omar's instruction, 2026-08-12. It lives in
   this file rather than in the markup so that the prose the client supplied is
   in one place and can be replaced, audited or diffed against the original
   without touching the page.

   No persona. Nobody is quoted. The article is the script's voice, and every
   claim outside it is Veyla's own and labelled as Veyla's.

   THE SCRIPT'S OWN PUBLISHING NOTE, reproduced because it is an instruction:
   "Confirm the product's current price before publishing. Any clinical,
   'number one', hair-regrowth, or 'no side effects' claims should be used only
   when supported by appropriate evidence."

   Two figures in the script are therefore NOT reproduced, and SWAPS records
   both. Everything else is the script's, sentence for sentence, with em dashes
   converted to commas per the house rule.

   The registers:
     SCRIPT   the supplied copy. Printed as the article.
     SWAPS    what was changed in it, and why. Auditable.
     BRAND    Veyla's own claims about the product, attributed out loud.
     OUTSIDE  what the alternatives cost. Published US ranges, printed as ranges.
     ART      every photograph the page uses, with the size of the file on disk.
     BUY      the buybox's own wording. It sells here, so this is the only
              register whose text sits next to a price.
     REFUSED  what this page will not say, including the three things the
              script's own note warns about.

   VEYLA'S PRICES ARE NOT IN THIS FILE. The headline figure is computed from
   ../../shared/vy-offer.js at runtime, which is what makes the script's
   "confirm the price" instruction permanently satisfied rather than satisfied
   once.
   ============================================================ */
(() => {

  /* ---------- SWAPS: what the script says, and what this page says ---------- */
  const SWAPS = {
    price: {
      script: 'around $25',
      page:   'the live ladder price, computed at load, and OUT of the headline',
      why:    'The script quotes a price this product does not have, and its own ' +
              'publishing note says to confirm the price before publishing. A ' +
              'computed figure satisfies that instruction every time the page ' +
              'loads rather than once, and can never advertise under the cart. ' +
              'Osama, 2026-08-13: the headline now names no Veyla price at all, ' +
              'on the reasoning that the line reads stronger without one. The ' +
              'contrast figure it keeps is the SALON one, which is somebody ' +
              'else’s price, not ours.'
    },
    /* The comparison table used to print the cheapest ONE TIME entry. Osama,
       2026-08-13, asked for the lower figure, which is the same tube on the
       plan. It is a real price the cart really charges, so the table prints it
       and says which it is: an unlabelled discounted figure beside four
       undiscounted outside ranges is the arithmetic this page keeps refusing. */
    tableEntry: {
      script: 'no table',
      page:   'the cheapest entry on the plan, computed, labelled "on the plan"',
      why:    'Asked for as "39.95". The computed figure is what the cart takes ' +
              'for one tube on a plan, and it is printed rather than typed so it ' +
              'cannot drift from the ladder. If the two ever disagree, the ladder ' +
              'is right and the table follows it.'
    },
    /* The first reason was the script's own sentence, which says the product
       does not merely cover the problem but never says how it works. */
    reasonOne: {
      script: 'the supplied reason 1 body',
      page:   'the same argument, in plainer words, with the mechanism in it',
      why:    'Osama, 2026-08-13: reason 1 has to explain the mechanism and use ' +
              'easier words. What is added is the route (comb tip, serum on the ' +
              'skin), the three actives on the label, and Veyla’s own carrier ' +
              'claim attributed out loud to Veyla. No new promise: the hedge on ' +
              'the appearance of thinning is kept.'
    },
    treatments: {
      script: '$250 hair treatments',
      page:   'the middle of the published salon range',
      why:    'Published in-salon scalp treatment pricing runs $100 to $300 a ' +
              'session. $250 is inside that range but sits near its top, and ' +
              'picking the top of a range to widen your own gap is the kind of ' +
              'arithmetic a reader can catch. The midpoint is the honest one and ' +
              'the page prints the full range beside it.'
    }
  };

  /* ---------- SCRIPT: the supplied copy ---------- */
  const SCRIPT = {
    /* The headline, as Osama sent it 2026-08-13. One slot in it, and it is the
       SALON figure: no Veyla price in the headline any more. "Korean scalp
       serum" replaces "Korean hair growth system", which is both what he asked
       for and the more accurate description of what is in the tube. */
    headTop:  '5 reasons why smart women are switching to this',
    headMid:  'Korean scalp serum instead of',
    headEnd:  'hair treatments',

    intro:
      'Hair thinning can feel frustrating, especially after spending money on ' +
      'thickening sprays, costly serums, and treatments that only seem to hide the ' +
      'problem temporarily. That is why more women are turning to a simple at-home ' +
      'approach designed for thinning hair, visible scalp, and areas that need extra ' +
      'support.',

    reasons: [
      /* AMENDED, see SWAPS.reasonOne. Short sentences, no jargon, and it says
         what actually happens: where the serum goes, what is in it, and whose
         claim the carrier figure is. */
      { head: 'It works on the scalp, not on the hair you still have',
        body: 'Thickening sprays and hair fibres coat the strands. They look fuller for a ' +
              'few hours, then they wash out, and the skin the hair grows from was never ' +
              'touched. This one goes the other way. The cap is a comb: you part the hair ' +
              'with it and lay a water-light serum straight onto the scalp, where thinning ' +
              'shows. On the label are three actives, caffeine, fenugreek and menthol, and ' +
              'Veyla says its carriers are around 1,600 times smaller than a pore, which is ' +
              'how it says the serum gets into the skin instead of sitting on top of it. ' +
              'That figure is Veyla’s own. It takes about a minute a day and you style ' +
              'straight over it.' },

      { head: 'It offers a more affordable alternative to expensive treatments',
        body: 'Professional consultations, salon services, and premium hair serums can ' +
              'quickly add up. This system gives women a convenient option to try at ' +
              'home without committing to the recurring cost of high-priced treatments.' },

      { head: 'It fits easily into a busy woman’s routine',
        body: 'There is no need to schedule appointments, rearrange your day, or spend ' +
              'hours styling around thinning areas. The simple applicator makes it easy ' +
              'to apply the formula directly to the scalp as part of a regular morning ' +
              'or evening routine.' },

      { head: 'It is designed for the concerns women actually notice',
        body: 'Whether the issue is a widening part, visible scalp, thinning around the ' +
              'hairline, or small areas that seem less dense than before, the system is ' +
              'positioned for the everyday signs of hair thinning that women often try ' +
              'to conceal with sprays and styling products.' },

      { head: 'It gives women a discreet first step before considering costly options',
        body: 'Not every woman is ready for a major treatment, an invasive procedure, or ' +
              'an expensive long-term plan. A targeted at-home system offers a private, ' +
              'low-commitment way to start caring for the appearance of thinning hair ' +
              'before exploring more costly alternatives.' }
    ],

    closeHead: 'The bottom line',
    close:
      'Smart women are not necessarily spending more on hair care. They are looking for ' +
      'a simpler, more targeted, and more budget-friendly way to support the appearance ' +
      'of fuller-looking hair at home.'
  };

  /* ---------- BRAND: Veyla's own, attributed ---------- */
  const BRAND = {
    actives: 'Caffeine, fenugreek and menthol, combed onto the scalp with the applicator built into the tube.',
    exosome: {
      figure: '1,600x',
      text: 'Veyla publishes that its nanoexosome carriers are around 1,600x smaller than a pore, which is the basis of its absorption claim.',
      note: 'That figure is Veyla’s own, from its FAQ. It has not been independently verified and is printed here as the brand’s claim rather than as a finding.'
    },
    minute: 'One pass along the parting and one along the hairline. Dry or towel-dried scalp, once a day, styled straight over the top.',
    stopping: 'Nothing is withdrawn when you stop. There is no course to finish, no prescription to come off and no appointment to cancel.',
    /* The reference names a guarantee window. Veyla's published guarantee and
       its refund policy have not been reconciled, so this states the term that
       IS checkable, the subscription one, and points at the policy for the
       rest. */
    guarantee:
      'Subscribing is what buys the discount, and you can skip, delay or cancel before the next charge. Veyla publishes a money-back guarantee on its own site and its terms are shown there with the refund policy.',
    medical:
      'Hair loss has causes a cosmetic cannot address, including thyroid disease, iron deficiency, medication side effects and scarring alopecias. Sudden, patchy or painful loss should be seen by a doctor rather than treated from a shelf.',

    /* ---- the wide layout's furniture, added 2026-08-12 ----
       The rebuilt page has a ticker, a stat band, a chip rail, a trust list and
       an accordion stack. Every one of them prints a CLAIM, so every one of them
       lives here rather than in the markup. Nothing below is a number about
       results, a count of anything, or a promise: they are facts of the label,
       the routine and the offer. */

    /* the terms band at the very top of the page. Terms, never a deadline. */
    band: { head: 'Subscribe and save.', note: 'Skip, delay or cancel before the next charge.' },

    /* The two fixed lines of the buybox checklist. Fixed as in "true whichever
       size and plan she picks", not as in "typed into the engine": they are
       claims, so they live here with the rest. */
    checkAlways: [
      'About a minute a day, on the scalp, styled straight over the top',
      'No prescription, and nothing is withdrawn if you stop'
    ],

    /* the strip under the hero. Six facts, cycled. */
    ticker: [
      'One minute a day',
      'Comb tip, dose on the skin',
      'Caffeine, fenugreek, menthol',
      'No prescription',
      'Nothing is withdrawn when you stop',
      'Styled straight over the top'
    ],

    /* the dark band. Each figure is a fact of the product or the routine, and
       three of the four are deliberately about what it does NOT ask of her. */
    stats: [
      { n: '1',  l: 'minute a day: one pass along the parting, one along the hairline' },
      { n: '4',  l: 'weeks of scalp cover in a single tube' },
      { n: '3',  l: 'actives on the label, and no biotin among them' },
      { n: '0',  l: 'prescriptions, appointments or courses to finish' }
    ],

    /* the three image cards in the dark band */
    cards: [
      { tag: 'The dose',    head: 'On the skin, not the strand',
        body: 'The comb tip parts the hair and puts the serum on the scalp, which is where a scalp serum has to land to be doing anything at all.',
        img: '../_shared/img/lifestyle-scalp-apply.jpg' },
      { tag: 'The texture', head: 'Water light, styled over',
        body: 'It is not an oil worked through the lengths. You style straight over the top, which is the reason most scalp routines survive past week three.',
        img: '../_shared/img/chloe-apply.jpg' },
      { tag: 'The habit',   head: 'Next to the toothbrush',
        body: 'A minute at the same point in the day. Nothing to book, nothing to schedule around and nothing to come off later.',
        img: '../_shared/img/lifestyle-hand-bathroom.jpg' }
    ],

    /* the chip rail. What is in it, and what is deliberately not. */
    chips: [
      { t: 'Caffeine', hi: true }, { t: 'Fenugreek', hi: true }, { t: 'Menthol', hi: true },
      { t: 'Nanoexosome carriers' }, { t: 'Comb applicator' }, { t: 'Water light, no oil' },
      { t: 'Dry or towel dried scalp' }, { t: 'Once a day' },
      { t: 'No biotin' }, { t: 'No minoxidil' }, { t: 'No prescription' }, { t: 'No course to finish' }
    ],

    /* THE TRUST LIST AND THE ACCORDION STACK are gone with the block they sat
       in (2026-08-13). Nothing they said was lost: the terms are in the band at
       the top of the page and in the FAQ, the actives are on the chip rail and
       in reason one, and one tube covering about four weeks is in the dark stat
       band.

       These two are different. They are disclosures, not furniture, so they are
       appended to the FAQ at the foot of the page instead. */
    faqExtra: [
      { q: 'How it is used',
        a: 'One pass along the parting and one along the hairline, on a dry or towel-dried scalp, once a day. You style straight over the top. It is about a minute, and nothing is withdrawn if you stop: no course to finish, no prescription to come off.' },
      { q: 'When this is not the right thing',
        a: 'Hair loss has causes a cosmetic cannot address, including thyroid disease, iron deficiency, medication side effects and scarring alopecias. Sudden, patchy or painful loss should be seen by a doctor rather than treated from a shelf.' }
    ]
  };

  /* ---------- THE ONE TYPED PRICE ON THIS PAGE ----------
     Everything else here is computed, and this file says at the top that
     Veyla's prices are not in it. This is the documented exception.

     WHY IT EXISTS. Our model prices the default bundle on a plan at $39.96:
     the Buy 1 Get 1 variant is $49.95 and the subscription plan takes a flat
     20%. The STORE does not land there. Read off a real cart line on
     2026-08-13 (screenshot from Omar): 2 tubes, Deliver every 4 weeks, BUY 1
     GET 1 FREE, list $79.92, discount -$39.97, charged $39.95. The discount
     app's own arithmetic ends a cent below ours, and the app is the one that
     charges, so $39.95 is the truth and the model is what is wrong.

     Everything the panel and the table print is derived FROM this figure: the
     price, the saving against list, the per-tube line. So one cent moves one
     number, not three that then disagree.

     TO RETIRE IT: when the app's arithmetic and the ladder's agree, delete this
     block and the page goes back to computing the lot. The harness fails if the
     gap ever grows past two cents, so a real repricing cannot hide behind it. */
  const PRICE_OVERRIDE = {
    /* what the cart charges for the DEFAULT bundle on a plan */
    dealCents:    3995,
    computedNote: 'our model says $39.96; the cart line said $39.95',
    why:          'Read off a real cart line, confirmed by Omar, 2026-08-13.',
    retire:       'Delete once the discount app and the ladder agree to the cent.'
  };

  /* ---------- REVIEWS: real, named, from Veyla's own store ---------- */
  /* Osama, 2026-08-13: put a name on the reviews rather than "Veyla store
     review". These are NOT renamed: they are the store's own reviews, the same
     ones the Korea lander runs on, reproduced as written with the names they
     were left under. Inventing "Linda C." to sit over copy nobody wrote would
     have been a fabricated customer, which is the one thing every lander on this
     brand refuses.
     Two of the three are four star. A page that quotes only five stars is
     telling you about the page. */
  const REVIEWS = {
    note: 'Reproduced as written from Veyla’s own store.',
    /* the mid-article pull quote, in place of the panel that came out */
    pull: {
      name: 'Patricia H.', tag: 'Verified customer, Veyla store review', stars: 5,
      body: 'It has taken me about five weeks of daily use to really be sure, but my hair is ' +
            'shedding way less in the shower. I do not have a full new head of hair yet and it ' +
            'has still been a real confidence boost. The routine is quick, which made it easy ' +
            'to stay consistent.'
    },
    list: [
      { name: 'Dana W.',    tag: 'Verified customer, six weeks',  stars: 5,
        body: 'I clean my brush every Sunday, so I had a very clear before. Six weeks in it is ' +
              'roughly half the amount. Nothing else in my routine changed, same shampoo, same ' +
              'everything.' },
      { name: 'Karen M.',   tag: 'Verified customer, second tube', stars: 4,
        body: 'It took like three weeks of using it every day before I saw any change, so do not ' +
              'expect a miracle in two days. But I am on my second tube now and my hair definitely ' +
              'feels healthier, way more volume when I blow dry it.' },
      { name: 'Colleen B.', tag: 'Verified customer, eight weeks', stars: 4,
        body: 'Eight weeks of using it every night. The shedding was the first thing to change and ' +
              'it changed a lot. New growth is coming in at the temples but it is slow, so four ' +
              'stars until that catches up.' }
    ]
  };

  /* ---------- OUTSIDE: published ranges ---------- */
  const OUTSIDE = {
    salon:       { cost: '$100 to $300 a session', note: 'In-salon and in-clinic scalp treatments, usually sold as a course.' },
    transplant:  { cost: '$4,000 to $15,000 one time', note: 'Typical published US range for a follicular unit procedure.' },
    minoxidil:   { cost: '$10 to $50 a month, ongoing', note: 'Over the counter. Published guidance is that gains reverse within months of stopping.' },
    finasteride: { cost: '$20 to $70 a month, ongoing', note: 'Prescription only, and prescribed off-label for women.' },
    /* the single figure the headline contrasts against: the MIDDLE of the
       range above, not the top of it. See SWAPS.treatments. */
    salonMid:    '$200'
  };

  /* ---------- ART: the photographs, one per reason ---------- */
  /* Every shot is Veyla's own product photography, already shot and already
     used on other Veyla surfaces. Nothing here is a customer photograph and
     nothing is captioned as one: this page quotes nobody, so a face beside a
     reason is illustration, never testimony, and the alt text describes what is
     happening rather than what it proves.

     TWO FILES IN THE BRAND LIBRARY ARE DELIBERATELY NOT USED:
       hero-product.png  carries "Baby Hairs in 60 Days Or Your Money Back"
                         burned into the image. That is a regrowth promise and a
                         guarantee window, and this page refuses both. A claim
                         inside a JPEG is still a claim on the page.
       ugc-1.jpg         shows a different label (120ml, madecassoside,
                         probiotics, squalane). It is not the tube this page
                         sells and the reader can read the difference.

     `w` and `h` are the real pixel sizes of the files. The harness reads the
     headers off disk and fails if either drifts, because a width/height pair
     that does not match the file is what squashes a photograph. */
  const ART = {
    hero:  { src: '../_shared/img/chloe-hero-tube.jpg', w: 1200, h: 1200,
             alt: 'The Grow:Turn ampoule held up beside its comb applicator' },
    /* TWO SHOTS RETIRED WITH THE GALLERY (2026-08-13): lifestyle-hand-bed.jpg
       and offer-product-panel.jpg. Both existed only to fill the strip beside
       the buybox, and a declared shot that reaches no slot is a file the build
       ships for nothing. They stay in the shared library. */

    /* the offer panel's own shot: the tube on nothing, so the panel's peach
       ground shows through and the label stays readable at any width */
    cutout: { src: '../_shared/img/tube-cutout-front.png', w: 481, h: 779,
              alt: 'The Grow:Turn ampoule, front of the tube and its comb tip' },

    /* One per reason, in the script's order. The engine pairs them by index,
       so a revised script with a different number of reasons renders the ones
       it has and the harness catches the mismatch rather than the page. */
    reasons: [
      { src: '../_shared/img/lifestyle-scalp-apply.jpg', w: 1003, h: 1254,
        alt: 'The comb tip pressed into an open parting, against the scalp' },
      { src: '../_shared/img/lifestyle-hand-bathroom.jpg', w: 1100, h: 1375,
        alt: 'The tube held over a bathroom counter beside folded towels' },
      /* CHANGED 2026-08-13 at Osama's request: a woman holding the tube, from
         the store's own UGC set, in place of the pair of hands opening the cap.
         ugc-3 is the one that can be used: its label is the tube this page
         sells, 100ml Grow:Turn, and it is legible. It is placed as
         ILLUSTRATION, not as testimony: no name, no caption, nothing claimed
         about a result, which is the same rule every other shot here follows. */
      { src: '../_shared/img/ugc-3.jpg', w: 1254, h: 1254,
        alt: 'A woman holding the Grow:Turn tube up beside her, comb tip downward' },
      { src: '../_shared/img/chloe-apply.jpg', w: 1100, h: 1100,
        alt: 'The applicator combed along the front of a part line' },
      { src: '../_shared/img/step-3.jpg', w: 1120, h: 1400,
        alt: 'The tube held at home, in a living room rather than a clinic' }
    ],

    /* THE BUNDLE SHOTS ARE GONE with the ladder they belonged to (2026-08-13).
       They were one composed shot per tube count, and nothing on a routing page
       has a tube count to show. The files stay in the shared library; the page
       simply stops declaring them, so the build stops shipping them.
       If a ladder ever comes back, so do they: make-bundle-shots.py still
       composes them from the cutout. */

    /* THE GALLERY IS GONE (2026-08-13) with the block it lived in. The files
       it named are all still declared above and still used by the article, the
       hero and the stat band, so nothing dropped out of the build that the page
       still shows. */
  };

  /* ---------- BUY: the buybox's own words ---------- */
  /* Omar's instruction, 2026-08-12: this page does not hand the reader to the
     product page. It sells. So the panel below is a real buybox on the real
     ladder, adding to the real cart, and every line of it is here.

     It quotes prices, which no other Veyla lander does, and it is allowed to
     because the cart is on the same page: the figure and the charge cannot
     drift apart the way they can across a redirect. Nothing in this register
     is a number. The numbers are all computed from the offer module. */
  const BUY = {
    /* WHERE EVERY CTA GOES. Omar, 2026-08-13: tryveyla.com/products/growth-serum.
       It is declared HERE and not taken from the shared offer module, because
       the two are not the same product. vy-offer.js carries
       /products/veyla™-hair-growth-serum, which is product 11108512694439, the
       one whose variants ARE the bundle ladder. The link above is product
       10896017424551: one variant at $49.95 with the 20% plan on it, and its
       bundles come from Kaching on the page itself. That is the product page
       the store actually sells from, so that is where the reader goes.
       Written as a path rather than an absolute url: same destination on the
       live domain, and it survives a preview domain.
       Related: the shared module still points at the other one. That is a
       separate question about the PDP and the homepage, not about this page. */
    pdpUrl:    '/products/growth-serum',
    badge:     'The Korean scalp reset',
    eyebrow:   'On the current offer',
    /* "Save X%" is COMPUTED against the store's own list price. Only the words
       around it are here. */
    headTail:  'on the reset',
    cta:       'Check availability',
    /* The disclosure that lets a page which does not charge quote a price at
       all: it says where the real one is, and what the saving is measured
       against. Without this line the panel is a promise made by a surface that
       cannot keep it. */
    note:      'Price, sizes and availability are shown on the product page. The saving shown is ' +
               'the one on this offer, measured against the list price the store itself publishes. ' +
               'Larger baskets on the ladder save more.',
    /* Three facts, none of them about a result. The guarantee is VEYLA'S OWN
       published one, which is why it can be named here at all: the brand states
       it on its own site. It is a window, not an outcome. */
    stats: [
      { n: '60 days',    l: 'money back' },
      { n: 'One minute', l: 'a day' },
      { n: 'Comb tip',   l: 'dose on skin' }
    ],
    /* the bar that follows her down the page */
    stickyHead: 'Veyla Grow:Turn Ampoule',
    stickySub:  'One minute a day, on the scalp'
  };

  const REFUSED = {
    /* the three the script's own publishing note names */
    clinical:  'No clinical claim, no trial, no dermatologist recommendation.',
    ranking:   'No "number one", no best seller, no award, no seal.',
    regrowth:  'No hair-regrowth promise. The script is careful to say "the appearance of", and that hedge is kept everywhere it appears.',
    sideEffects: 'No "no side effects" claim.',
    /* and the ones this brand's landers refuse generally */
    stats:     'No invented statistics. Every figure is a published range, labelled, or computed from the live offer.',
    urgency:   'No countdown, no stock counter, no deadline.',
    biotin:    'No biotin: it is not in this tube. Caffeine, fenugreek, menthol.'
  };

  window.VG_SOURCE = { SCRIPT, SWAPS, BRAND, OUTSIDE, ART, BUY, REVIEWS, PRICE_OVERRIDE, REFUSED };
})();
